import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import MetricCard from "../../../common_components/MetricCard"; // Adjust path as needed
import AnalyticsLineChart from "../../../common_components/AnalyticsLineChart/AnalyticsLineChart";
import { formatStat } from "../../../helper/statsFormatter";
// Helper to get icon by title
const getIconByTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes("roas"))
        return _jsx("img", { src: "/assets/stats/roas.svg", alt: "roas" });
    if (t.includes("spend"))
        return _jsx("img", { src: "/assets/stats/spend.svg", alt: "spend" });
    if (t.includes("revenue"))
        return _jsx("img", { src: "/assets/stats/revenue.svg", alt: "revenue" });
    if (t.includes("cpc"))
        return _jsx("img", { src: "/assets/stats/cpc.svg", alt: "cpc" });
    if (t.includes("session"))
        return _jsx("img", { src: "/assets/stats/cpc.svg", alt: "sessions" });
    if (t.includes("click"))
        return _jsx("img", { src: "/assets/stats/cpc.svg", alt: "clicks" });
    if (t.includes("sale"))
        return _jsx("img", { src: "/assets/stats/sale.svg", alt: "sales" });
    return _jsx("img", { src: "/assets/stats/revenue.svg", alt: "default" });
};
// Helper to calculate percentage and trend
const calculateTrend = (current, previous, isInverse = false) => {
    const valNum = Number(current ?? 0);
    const prevNum = Number(previous ?? 0);
    const diff = valNum - prevNum;
    let percent;
    if (prevNum === 0 && valNum === 0) {
        percent = 0;
    }
    else if (prevNum === 0 && valNum !== 0) {
        percent = 100;
    }
    else {
        percent = (diff / prevNum) * 100;
    }
    const isDecrease = diff < 0;
    let trend;
    if (percent === 0) {
        trend = "neutral";
    }
    else {
        trend = isDecrease ? "down" : "up";
    }
    return {
        percentageText: `${Math.round(Math.abs(percent))}%`,
        trend,
        isInverse
    };
};
const SportPerformance = ({ data, entities, loading, activeTab, overallData, clientId, chartFirst = false, }) => {
    if (!data) {
        return (_jsx(Box, { sx: { p: 4, textAlign: "center", color: "#64748b" }, children: _jsx(Typography, { children: "No Data Available" }) }));
    }
    let tabKey = 'all';
    const lowerTab = activeTab.toLowerCase();
    if (lowerTab.includes('meta'))
        tabKey = 'meta';
    else if (lowerTab.includes('adword') || lowerTab.includes('google'))
        tabKey = 'adword';
    else if (lowerTab.includes('bing'))
        tabKey = 'bing';
    else if (lowerTab === 'shopify')
        tabKey = 'shopify';
    let chartData = null;
    let statsContainer = null;
    if (tabKey === 'meta') {
        chartData = data.charts?.meta;
        statsContainer = data.charts?.meta?.stats;
    }
    else if (tabKey === 'adword') {
        chartData = data.charts?.adword;
        statsContainer = data.charts?.adword?.stats;
    }
    else {
        chartData = data.charts?.all;
        statsContainer = data.charts?.all?.stats;
    }
    if (!statsContainer)
        return null;
    const current = statsContainer.current;
    const comparison = statsContainer.comparison;
    const hasComparison = !!comparison;
    const base = hasComparison ? (comparison || statsContainer.base) : null;
    const isAllTab = tabKey === 'all';
    // Helper to build MetricCard props
    const buildCardProps = (title, value, previous, isInverse = false, formatType, tooltipText) => {
        const formattedValue = formatStat(value ?? 0, formatType || title);
        const formattedPrevious = formatStat(previous ?? 0, formatType || title);
        const { percentageText, trend, isInverse: returnedIsInverse } = calculateTrend(value, previous, isInverse);
        return {
            title,
            icon: getIconByTitle(title),
            value: formattedValue,
            comparisonValue: hasComparison ? formattedPrevious : undefined,
            percentageText: hasComparison ? percentageText : undefined,
            trend: hasComparison ? trend : undefined,
            isInverse: returnedIsInverse,
            tooltipText
        };
    };
    // ✅ Build chart series based on tab
    const { chartSeries, comparisonData } = useMemo(() => {
        const series = [];
        if (!chartData?.series) {
            return { chartSeries: [], comparisonData: undefined };
        }
        // ============ ALL TAB - Only these 5 metrics ============
        if (isAllTab) {
            if (chartData.series.revenue || chartData.series.channel_revenue) {
                series.push({
                    name: "Channels Revenue",
                    data: chartData.series.revenue || chartData.series.channel_revenue,
                    color: "#EC4899"
                });
            }
            if (chartData.series.shopify_revenue) {
                series.push({
                    name: "Shopify Revenue",
                    data: chartData.series.shopify_revenue,
                    color: "#3B82F6"
                });
            }
            if (chartData.series.spend) {
                series.push({
                    name: "Spend",
                    data: chartData.series.spend,
                    color: "#5F8F6B"
                });
            }
            if (chartData.series.roas || chartData.series.roas_channel) {
                series.push({
                    name: "Channels ROAS",
                    data: chartData.series.roas || chartData.series.roas_channel,
                    color: "#6366f1",
                    yAxisIndex: 1
                });
            }
            if (chartData.series.roas_shopify) {
                series.push({
                    name: "ROAS Shopify",
                    data: chartData.series.roas_shopify,
                    color: "#8B5CF6",
                    yAxisIndex: 1
                });
            }
        }
        // ============ META TAB ============
        if (tabKey === 'meta') {
            if (chartData.series.revenue || chartData.series.channel_revenue) {
                series.push({
                    name: "Channels Revenue",
                    data: chartData.series.revenue || chartData.series.channel_revenue,
                    color: "#EC4899"
                });
            }
            if (chartData.series.spend) {
                series.push({
                    name: "Spend",
                    data: chartData.series.spend,
                    color: "#5F8F6B"
                });
            }
            if (chartData.series.roas) {
                series.push({
                    name: "ROAS",
                    data: chartData.series.roas,
                    color: "#6366f1",
                    yAxisIndex: 1
                });
            }
            if (chartData.series.outbound_clicks) {
                series.push({
                    name: "Outbound Clicks",
                    data: chartData.series.outbound_clicks,
                    color: "#14b8a6"
                });
            }
        }
        // ============ ADWORD TAB ============
        if (tabKey === 'adword') {
            if (chartData.series.revenue || chartData.series.channel_revenue) {
                series.push({
                    name: "Channels Revenue",
                    data: chartData.series.revenue || chartData.series.channel_revenue,
                    color: "#EC4899"
                });
            }
            if (chartData.series.spend) {
                series.push({
                    name: "Spend",
                    data: chartData.series.spend,
                    color: "#5F8F6B"
                });
            }
            if (chartData.series.roas) {
                series.push({
                    name: "ROAS",
                    data: chartData.series.roas,
                    color: "#6366f1",
                    yAxisIndex: 1
                });
            }
            if (chartData.series.clicks) {
                series.push({
                    name: "Clicks",
                    data: chartData.series.clicks,
                    color: "#0ea5e9"
                });
            }
        }
        // ============ BUILD COMPARISON DATA ============
        let compData = undefined;
        if (chartData?.comparison_labels && chartData?.comparison_series) {
            if (isAllTab) {
                compData = {
                    labels: chartData.comparison_labels,
                    series: {
                        channel_revenue: chartData.comparison_series.revenue || chartData.comparison_series.channel_revenue,
                        shopify_revenue: chartData.comparison_series.shopify_revenue,
                        spend: chartData.comparison_series.spend,
                        roas_channel: chartData.comparison_series.roas || chartData.comparison_series.roas_channel,
                        roas_shopify: chartData.comparison_series.roas_shopify,
                        cpc: chartData.comparison_series.cpc,
                        sessions: chartData.comparison_series.sessions,
                    },
                };
            }
            else if (tabKey === 'meta') {
                compData = {
                    labels: chartData.comparison_labels,
                    series: {
                        channel_revenue: chartData.comparison_series.revenue || chartData.comparison_series.channel_revenue,
                        spend: chartData.comparison_series.spend,
                        roas: chartData.comparison_series.roas,
                        cpc: chartData.comparison_series.cpc,
                        outbound_clicks: chartData.comparison_series.outbound_clicks,
                    },
                };
            }
            else if (tabKey === 'adword') {
                compData = {
                    labels: chartData.comparison_labels,
                    series: {
                        channel_revenue: chartData.comparison_series.revenue || chartData.comparison_series.channel_revenue,
                        spend: chartData.comparison_series.spend,
                        roas: chartData.comparison_series.roas,
                        cpc: chartData.comparison_series.cpc,
                        clicks: chartData.comparison_series.clicks,
                    },
                };
            }
        }
        return { chartSeries: series, comparisonData: compData };
    }, [chartData, tabKey, isAllTab]);
    const cardSx = chartFirst ? { minWidth: 0, flex: "unset", width: "100%" } : {};
    const statsCards = (_jsxs(Box, { sx: chartFirst ? {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            width: "100%",
            gap: "10px",
            padding: "10px",
        } : {
            display: "flex",
            flexWrap: "wrap",
            width: "100%",
            justifyContent: "space-between",
            gap: "10px",
            alignItems: "stretch",
            mb: 2,
            minHeight: "150px",
        }, children: [_jsx(MetricCard, { sx: cardSx, ...buildCardProps(isAllTab ? "Channels Revenue" : "Revenue", current.revenue ?? current.channel_revenue ?? 0, base?.revenue ?? base?.channel_revenue, false, "revenue") }), _jsx(MetricCard, { sx: cardSx, ...buildCardProps("Spend", current.spend ?? 0, base?.spend, true, "spend") }), _jsx(MetricCard, { sx: cardSx, ...buildCardProps("Channels ROAS", current.roas ?? current.roas_channel ?? 0, base?.roas ?? base?.roas_channel, false, "roas", "Channels Revenue / Spend") }), isAllTab && (_jsx(MetricCard, { sx: cardSx, ...buildCardProps("Shopify ROAS", current.roas_shopify ?? 0, base?.roas_shopify, false, "roas", "Shopify Revenue / Spend") })), tabKey !== 'shopify' && (tabKey === 'meta' ? (_jsx(MetricCard, { sx: cardSx, ...buildCardProps("CPOC", (current.outbound_clicks > 0 ? (current.spend || 0) / current.outbound_clicks : 0), (base?.outbound_clicks > 0 ? (base?.spend || 0) / base?.outbound_clicks : null), true, "cpc", "Spend / Outbound Clicks") })) : (_jsx(MetricCard, { sx: cardSx, ...buildCardProps("CPC", current.cpc ?? 0, base?.cpc, true, "cpc", "Spend / Clicks") }))), tabKey === 'meta' && (_jsxs(_Fragment, { children: [_jsx(MetricCard, { sx: cardSx, ...buildCardProps("OCTR", (current.impressions > 0 ? (current.outbound_clicks || 0) / current.impressions * 100 : 0), (base?.impressions > 0 ? (base?.outbound_clicks || 0) / base?.impressions * 100 : null), false, "percent_of_spend", "Outbound Clicks / Impressions * 100") }), _jsx(MetricCard, { sx: cardSx, ...buildCardProps("CT ROAS", current.roas_ct ?? 0, base?.roas_ct, false, "roas", "CT Revenue / Spend") }), _jsx(MetricCard, { sx: cardSx, ...buildCardProps("VT ROAS", current.roas_vt ?? 0, base?.roas_vt, false, "roas", "VT Revenue / Spend") })] })), (tabKey === 'adword' || tabKey === 'bing') && current.clicks != null && (_jsxs(_Fragment, { children: [_jsx(MetricCard, { sx: cardSx, ...buildCardProps("Clicks", current.clicks ?? 0, base?.clicks, false, "number") }), _jsx(MetricCard, { sx: cardSx, ...buildCardProps("CTR", (current.impressions > 0 ? (current.clicks || 0) / current.impressions * 100 : 0), (base?.impressions > 0 ? (base?.clicks || 0) / base?.impressions * 100 : null), false, "percent_of_spend", "Clicks / Impressions * 100") })] })), current.percent_of_spend != null && (_jsx(MetricCard, { sx: cardSx, ...buildCardProps("% of Spend", current.percent_of_spend, base?.percent_of_spend, false, "percent_of_spend") })), current.percent_of_sale != null && (_jsx(MetricCard, { sx: cardSx, ...buildCardProps("% of Sales", current.percent_of_sale, base?.percent_of_sale, false, "percent_of_sale") }))] }));
    const chart = (_jsx(Box, { sx: { mt: chartFirst ? 0 : 4, mb: chartFirst ? 2 : 0 }, children: _jsx(AnalyticsLineChart, { categories: chartData?.labels, series: chartSeries, height: 350, showLegend: true, comparison: comparisonData, forceComparisonMatchBaseSeries: true, compact: chartFirst, legendVariant: "pills" }) }));
    return (_jsx(Box, { sx: { p: 1, borderRadius: 3 }, children: chartFirst ? (_jsxs(_Fragment, { children: [chart, statsCards] })) : (_jsxs(_Fragment, { children: [statsCards, chart] })) }));
};
export default SportPerformance;
